import { useCallback, useState } from 'react';
import { utcToZonedTime } from 'date-fns-tz';
import { CalendarDay } from '../../components/core/Calendar/Calendar.types';
import { generateDateKey } from '../../modules/availability/caching';
import { getDayOfWeekInTimezone } from '../../utils/timezoneUtils';
import { generateCacheKey } from '../../utils/cacheKeyGenerator';
// Debug system removed

interface UseCalendarAvailabilityProps {
  meetingTypeId?: string | number;
  meetingType?: any; // Full meeting type object with availabilitySchedule
  selectedDate: Date | null;
  cachedMonthSlots: Record<string, any>;
  selectedTimezone?: string; // Add timezone for proper cache key generation
  loadingSlots?: boolean; // Add loading state from parent
  username?: string;
  token?: string | null;
  duration?: number; // Add duration for proper cache key generation
  selectedProviderId?: number | null; // Provider ID for service-type cache key matching
}

/**
 * useCalendarAvailability Hook
 * 
 * Handles the logic for determining date availability for the calendar
 * This separates the business logic from the UI components
 */
export const useCalendarAvailability = ({
  meetingTypeId,
  meetingType,
  selectedDate,
  cachedMonthSlots,
  selectedTimezone,
  loadingSlots = false,
  username,
  token,
  duration,
  selectedProviderId,
}: UseCalendarAvailabilityProps) => {
  const [loadingAvailability] = useState<boolean>(false);

  // Function to determine if a date has available slots
  const isDateAvailable = useCallback((date: Date): boolean => {
    if (!meetingTypeId) {
      return false;
    }

    // Don't allow dates in the past (properly compare years first)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // First compare years to avoid year 2025 being marked as "in the past"
    if (date.getFullYear() < today.getFullYear()) {
      return false;
    } else if (date.getFullYear() === today.getFullYear()) {
      // Only if in the same year, compare the full dates
      if (date < today) {
        return false;
      }
    }

    // CRITICAL FIX: Skip availabilitySchedule check in preview mode (meetingTypeId === -1)
    // In preview mode, the availabilitySchedule is stale/legacy data that doesn't reflect
    // the currently selected schedule. We should rely solely on the API-returned slots.
    const isPreviewMode = meetingTypeId === -1 || meetingTypeId === '-1';

    // Check if the day of week is allowed according to meeting type availability schedule
    // ONLY in non-preview mode where availabilitySchedule is reliable
    if (!isPreviewMode && meetingType && meetingType.availabilitySchedule) {
      // FIXED: Use timezone-aware day-of-week calculation to match server logic
      const dayOfWeek = getDayOfWeekInTimezone(date, selectedTimezone || 'UTC');

      // Get all allowed days from all availability schedules
      const allowedDays = meetingType.availabilitySchedule
        .flatMap((schedule: any) => schedule.days)
        .filter((day: number) => typeof day === 'number');

      // If we have allowed days defined and current day is not in the list, it's not available
      if (allowedDays.length > 0 && !allowedDays.includes(dayOfWeek)) {
        return false;
      }
    }

    // Check cached availability - be restrictive for better UX
    // FIXED: Use the same date key generation logic as BookingDataProvider.tsx getSlotsForDate
    // This ensures cache keys match between storage and retrieval
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
    const dateInTargetTimezone = utcToZonedTime(normalizedDate, selectedTimezone || 'UTC');
    const dateYear = dateInTargetTimezone.getFullYear();
    const dateMonth = String(dateInTargetTimezone.getMonth() + 1).padStart(2, '0');
    const dateDay = String(dateInTargetTimezone.getDate()).padStart(2, '0');
    const dateKey = `${dateYear}-${dateMonth}-${dateDay}`;

    // CRITICAL FIX: Use shared generateCacheKey function to match BookingDataProvider
    // This ensures cache keys are identical for storage and retrieval, especially in preview mode
    const effectiveDuration = duration || meetingType?.duration?.[0] || 30;
    const monthKey = generateCacheKey(
      typeof meetingTypeId === 'string' ? parseInt(meetingTypeId) : meetingTypeId,
      date.getFullYear(),
      date.getMonth() + 1,
      selectedTimezone || 'UTC',
      effectiveDuration,
      meetingType ? {
        bufferTimeBefore: meetingType.bufferTimeBefore,
        bufferTimeAfter: meetingType.bufferTimeAfter,
        slotIncrementMinutes: meetingType.slotIncrementMinutes,
        minimumBookingNotice: meetingType.minimumBookingNotice,
        maximumBookingWindow: meetingType.maximumBookingWindow,
      } : undefined,
      meetingType?.scheduleId,  // CRITICAL: Include scheduleId to prevent cache collisions
      selectedProviderId  // CRITICAL: Include providerId to prevent cache collisions when switching providers
    );

    // If we don't have cached data for this month, check loading state
    // Be conservative during timezone switches to prevent weekends showing as available
    if (!cachedMonthSlots[monthKey]) {
      // During loading (especially timezone switches), don't show any dates as available
      // This prevents weekends from appearing selectable
      if (loadingSlots || loadingAvailability) {
        return false;
      }
      // If not loading, we haven't started fetching yet, so assume unavailable
      return false;
    }

    // If we have cached data but this month has errors, mark as unavailable
    if (cachedMonthSlots[monthKey].error) {
      return false; // If there was an error loading slots, don't allow selection
    }

    // If month data is incomplete and we're currently loading, allow optimistic availability
    if (!cachedMonthSlots[monthKey].isComplete && loadingAvailability) {
      // Check if we have partial data for this specific date
      const slots = cachedMonthSlots[monthKey].slots?.[dateKey];
      if (slots !== undefined) {
        return slots.length > 0; // Use available partial data
      }
      return true; // Optimistic: assume available while loading
    }

    // If month data is incomplete but we're not loading, this might be a partial cache
    if (!cachedMonthSlots[monthKey].isComplete) {
      // Check if we have data for this specific date even if month is incomplete
      const slots = cachedMonthSlots[monthKey].slots?.[dateKey];
      if (slots === undefined) {
        return false; // No data for this date yet
      }
      return slots.length > 0; // Has slots for this specific date
    }

    // Month is complete, check if there are slots for this date
    const slots = cachedMonthSlots[monthKey].slots[dateKey];
    const hasSlots = !!slots && slots.length > 0;
    
    return hasSlots;
  }, [meetingTypeId, meetingType, cachedMonthSlots, selectedTimezone, loadingAvailability, duration, selectedProviderId]);

  // Function to enhance calendar days with availability information
  const enhanceDayWithAvailability = useCallback((day: CalendarDay): CalendarDay => {
    // If day is not in current month or in the past, no need to check availability
    if (!day.isCurrentMonth || !day.isSelectable) {
      return day;
    }

    const hasAvailability = isDateAvailable(day.date);
    
    return {
      ...day,
      // Make dates without availability non-selectable
      isSelectable: day.isSelectable && hasAvailability,
      // Add availability to metadata
      metadata: {
        ...day.metadata,
        hasAvailability,
      }
    };
  }, [isDateAvailable]);

  // Function to get custom day class names based on availability
  const getAvailabilityDayClassNames = useCallback((day: CalendarDay): string => {
    if (!day.isCurrentMonth) {
      return '';
    }

    // Directly check availability instead of relying on metadata
    const hasAvailability = isDateAvailable(day.date);
    
    // Check if data is still loading for this date
    if (meetingType && day.isSelectable && selectedTimezone) {
      const effectiveDur = duration || meetingType?.duration?.[0] || 30;
      const monthKey = generateCacheKey(
        typeof meetingType.id === 'string' ? parseInt(meetingType.id) : meetingType.id,
        day.date.getFullYear(),
        day.date.getMonth() + 1,
        selectedTimezone || 'UTC',
        effectiveDur,
        meetingType ? {
          bufferTimeBefore: meetingType.bufferTimeBefore,
          bufferTimeAfter: meetingType.bufferTimeAfter,
          slotIncrementMinutes: meetingType.slotIncrementMinutes,
          minimumBookingNotice: meetingType.minimumBookingNotice,
          maximumBookingWindow: meetingType.maximumBookingWindow,
        } : undefined,
        meetingType?.scheduleId,
        selectedProviderId
      );

      // Check if we're still loading data for this month
      if (!cachedMonthSlots[monthKey] || (!cachedMonthSlots[monthKey].isComplete && loadingAvailability)) {
        return 'text-gray-500 opacity-50 cursor-wait'; // Loading state
      }
    }
    
    // Check if this is an unavailable date (in current month but no availability)
    if (day.isCurrentMonth && !hasAvailability) {
      // Unavailable dates get grey styling like past dates
      return 'text-gray-400 cursor-not-allowed opacity-60';
    }
    
    // Available dates with confirmed time slots
    if (day.isSelectable && hasAvailability) {
      return 'bg-blue-100 hover:bg-blue-200 font-bold text-blue-900';
    }
    
    return '';
  }, [isDateAvailable, meetingType, selectedTimezone, cachedMonthSlots, loadingAvailability, duration, selectedProviderId]);

  return {
    isDateAvailable,
    enhanceDayWithAvailability,
    getAvailabilityDayClassNames,
    loadingAvailability
  };
};

export default useCalendarAvailability; 